import { Country, Checkout } from '@afterpaytouch/portal-api/types'
import { BiometricsCheckTrigger, InitialData, OtpLoginRouteAndTriggerResult, ConvergenceRollout } from '../../../model'
import { getValidUserEmailAndMobile } from '../../login-identifier'
import { determineRedirectCountry } from '../countryRedirection'
import { createBiometricEligibilityPayload } from '../biometricEligibility'
import { SequentialLookupRequest } from './sequentialLookupOrchestrator'

export interface LookupDataPreparationRequest {
  checkout: Checkout
  country: Country
  validInitialData: InitialData
  browserCapabilities: {
    canSupportBiometric: boolean
    isAuthenticatorAvailable: boolean
  }
  usernameFromCookie: string | null
  biometricsEligibleAndRegisteredCheckTrigger: BiometricsCheckTrigger
  otpLoginRouteAndTrigger: OtpLoginRouteAndTriggerResult
  rollout: ConvergenceRollout
}

/**
 * Prepares all necessary data for the email/mobile lookup flow
 * Extracts user identification data, determines redirection needs, and creates biometric payload
 */
export const prepareLookupData = (request: LookupDataPreparationRequest): Omit<SequentialLookupRequest, 'checkout'> => {
  const {
    checkout,
    country,
    validInitialData,
    browserCapabilities,
    usernameFromCookie,
    biometricsEligibleAndRegisteredCheckTrigger,
    otpLoginRouteAndTrigger,
    rollout,
  } = request

  // Determine if country redirection is needed
  const redirectCountry = determineRedirectCountry({
    shippingCountryCode: checkout.shippingAddress?.countryCode,
    billingCountryCode: checkout.billingAddress?.countryCode,
    merchant: checkout.merchant,
    currentCountry: country,
  })

  // Extract user identification data
  const crossRegionUserSignInEmail = checkout.crossRegionUserSignIn?.consumerEmail
  const { email, mobileNumber } = getValidUserEmailAndMobile({
    crossRegionUserSignInEmail,
    checkoutEmail: checkout.consumer?.email,
    checkoutMobile: checkout.consumer?.phoneNumber,
    usernameFromCookie,
  })

  // Create biometric eligibility payload
  const isBiometricEligiblePayload = createBiometricEligibilityPayload({
    browserCanSupportBiometric: browserCapabilities.canSupportBiometric,
    isAuthenticatorAvailable: browserCapabilities.isAuthenticatorAvailable,
    initialData: validInitialData,
  })

  return {
    email,
    mobileNumber,
    country,
    redirectCountry,
    biometricCheckTrigger: biometricsEligibleAndRegisteredCheckTrigger,
    isBiometricEligiblePayload,
    otpLoginRouteAndTrigger,
    rollout,
  }
}
